import { FC, Fragment, useRef } from 'react';
import {
  Map,
  APILoader,
  NavigationControl,
  PanoramaControl,
  MapTypeControl,
  useMapContext,
  Provider,
  type MapRefs,
  type MapProps,
} from '@uiw/react-baidu-map';
import { BaiduMapProps, MapComponentsProps } from './BaiduMap.types';
import './BaiduMap.styles.css';

const NAVIGATION_OFFSET = { width: 10, height: 10 };
const MAP_TYPE_OFFSET = { width: 10, height: 10 };

const MapComponents: FC<MapComponentsProps> = ({
  navigationProps,
  mapControlProps,
  children,
  mapTypeControl = true,
  navigationControl = true,
  panoramaControl = false,
  isMapTypeControlDropdown = false,
}) => {
  const { BMap } = useMapContext();
  // Checking if the map api is loaded by the APILoader
  if (BMap)
    return (
      <>
        <MapTypeControl
          visiable={mapTypeControl}
          anchor={BMAP_ANCHOR_TOP_LEFT}
          offset={MAP_TYPE_OFFSET}
          {...(isMapTypeControlDropdown ? { type: BMAP_MAPTYPE_CONTROL_DROPDOWN } : {})}
          {...mapControlProps}
        />
        {panoramaControl && <PanoramaControl />}
        <NavigationControl
          anchor={BMAP_ANCHOR_BOTTOM_RIGHT}
          type={BMAP_NAVIGATION_CONTROL_ZOOM}
          offset={NAVIGATION_OFFSET}
          visiable={navigationControl}
          {...navigationProps}
        />
        {children}
      </>
    );
  return <Fragment />;
};

const BaiduMap: FC<BaiduMapProps> = ({
  apiKey,
  center,
  zoom,
  style,
  getMapRef,
  enableScrollWheelZoom = true,
  mapProps,
  autoCenterOnResize,
  ...props
}) => {
  const lastInstanceRef = useRef<MapRefs | null>(null);

  const internalMapProps: MapProps = {
    ...mapProps,
    onReSize: event => {
      mapProps?.onReSize?.(event);

      // Only run auto-center logic when `center` is an object with lng/lat
      if (!autoCenterOnResize || !(center instanceof Object)) return;

      const instance = lastInstanceRef.current || null;
      if (!instance?.map || !instance?.BMap) return;

      const { map, BMap } = instance;

      const { width, height } = event?.size || {};
      if (!width || !height) return;

      const markerPoint = new BMap.Point(center.lng, center.lat);

      const markerPixel = map.pointToPixel(markerPoint);

      const viewportCenterPixel = new BMap.Pixel(width / 2, height / 2);

      const dx = viewportCenterPixel.x - markerPixel.x;
      const dy = viewportCenterPixel.y - markerPixel.y;

      map.panBy(dx, dy, { noAnimation: true });
    },
  };

  const handleMapRef = (mapRefs: MapRefs | null) => {
    if (mapRefs?.map) {
      lastInstanceRef.current = mapRefs;
      if (getMapRef) {
        getMapRef(mapRefs);
      }
    }
  };

  return (
    <APILoader akay={apiKey} version="3.0">
      <Provider>
        <>
          <Map
            ref={handleMapRef}
            center={center}
            zoom={zoom}
            style={style}
            enableScrollWheelZoom={enableScrollWheelZoom}
            enableDoubleClickZoom
            enablePinchToZoom
            enableDragging
            enableAutoResize
            {...internalMapProps}
          >
            <MapComponents {...props} />
          </Map>
        </>
      </Provider>
    </APILoader>
  );
};

export default BaiduMap;
